import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { Label } from '@/components/ui/label'
import { CustomerNotificationSwitch } from '@/components/orders/CustomerNotificationSwitch'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'
import { type ReactNode, useId } from 'react'

type FulfillmentConfirmationSummaryProps = {
  statusLabel: string
  /** Replaces the "Set status to {statusLabel}" line. */
  statusText?: ReactNode
  /** `null` hides the summary line. */
  summaryText?: string | null
  icon?: ReactNode
  notifyCustomer?: boolean
  notificationLabel?: string | null
  onNotifyCustomerChange: (notifyCustomer: boolean) => void
  showNotificationSwitch?: boolean
  showCustomerNote?: boolean
  customerNote?: string
  onCustomerNoteChange?: (customerNote: string) => void
  disabled?: boolean
}

function FulfillmentConfirmationSummary({
  statusLabel,
  statusText,
  summaryText = 'This action cannot be undone.',
  icon = <Check className="size-4" />,
  notifyCustomer = true,
  notificationLabel,
  onNotifyCustomerChange,
  showNotificationSwitch = true,
  showCustomerNote = false,
  customerNote = '',
  onCustomerNoteChange,
  disabled = false,
}: FulfillmentConfirmationSummaryProps) {
  const noteId = useId()

  return (
    <Card size="sm" surface="brandSubtle">
      <CardContent size="sm">
        {summaryText ? <Typography variant="label">{summaryText}</Typography> : null}
        <div className={cn('grid gap-2 text-sm text-muted-foreground', summaryText ? 'mt-2' : '')}>
          <span className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0 text-success">{icon}</span>
            {statusText ?? <>Set status to {statusLabel}</>}
          </span>
          {showNotificationSwitch ? (
            <CustomerNotificationSwitch
              notifyCustomer={notifyCustomer}
              onNotifyCustomerChange={onNotifyCustomerChange}
              notificationLabel={notificationLabel}
              disabled={disabled}
            />
          ) : null}
        </div>
        {showCustomerNote && notifyCustomer ? (
          <div className="mt-3 border-t border-primary/15 pt-3">
            <Label htmlFor={noteId}>
              Note to customer <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Textarea
              id={noteId}
              className="mt-2 field-sizing-fixed"
              value={customerNote}
              maxLength={500}
              rows={3}
              placeholder="e.g. Left with the front desk. We hope they love it!"
              disabled={disabled}
              onChange={(event) => onCustomerNoteChange?.(event.target.value)}
            />
            <Typography variant="caption" className="mt-1.5 block">
              Included in the delivery email from your shop.
            </Typography>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

export default FulfillmentConfirmationSummary
